import { RSAA } from 'redux-api-middleware'
import { makePaginationParams } from 'sidecar-utilities'
import { transformPracticeToProvider } from 'sidecar-member-components/utils'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'

import { types, initialState } from 'store/Bundles'

// Helper functions for validation
const sanitizeString = (value, regex) => {
  if (!value) return null
  const sanitized = value.replace(regex, '')
  return sanitized || null
}

const sanitizeNumber = (value, validator) => {
  if (typeof value === 'number' && !isNaN(value) && validator(value)) {
    return value.toString()
  }
  return null
}

// Configuration for each parameter
const paramConfig = {
  bundleCode: { type: 'string', regex: /[^a-zA-Z0-9_-]/g },
  zip: { type: 'string', regex: /[^0-9-]/g },
  insuranceFilingUuid: { type: 'string', regex: /[^a-zA-Z0-9_-]/g },
  specialties: { type: 'string', regex: /[^a-zA-Z0-9,_\s\-&]/g },
  planId: { type: 'string', regex: /[^a-zA-Z0-9_-]/g },
  query: { type: 'string', regex: /[^\w\s.,\-@]/g, trim: true },
  sortBy: { type: 'string', regex: /[^\w\s.,\-@]/g, trim: true },
  lat: { type: 'number', validator: val => Math.abs(val) <= 90 },
  lon: { type: 'number', validator: val => Math.abs(val) <= 180 },
  radius: { type: 'number', validator: val => val > 0 },
  // Bounding box parameters for map-based search
  neLat: { type: 'number', validator: val => Math.abs(val) <= 90 },
  neLon: { type: 'number', validator: val => Math.abs(val) <= 180 },
  swLat: { type: 'number', validator: val => Math.abs(val) <= 90 },
  swLon: { type: 'number', validator: val => Math.abs(val) <= 180 },
  // Monitoring parameters for search endpoint observability
  requestId: { type: 'string', regex: /[^a-zA-Z0-9_-]/g },
  zoomLevel: { type: 'number', validator: val => val >= 0 && val <= 22 },
  progressiveSearch: { type: 'number', validator: val => val >= 0 },
  zoomTrigger: { type: 'string', regex: /[^a-z]/g },
}

export const sanitizeParams = input => {
  const params = {}

  Object.entries(input).forEach(([key, value]) => {
    const config = paramConfig[key]
    if (!config) return

    let sanitized
    if (config.type === 'string') {
      const val = config.trim && value ? value.trim() : value
      sanitized = sanitizeString(val, config.regex)
    } else if (config.type === 'number') {
      sanitized = sanitizeNumber(value, config.validator)
    }

    if (sanitized) params[key] = sanitized
  })

  return params
}

export const fetchProviderRates = requestParams => ({
  [RSAA]: {
    endpoint: ({ Bundles: { rates } }) => {
      const memberUuid = webStorage.getItem('sub')
      return getApiPath(
        'doc',
        `bundle/rates/providers${makeQueryParams({
          ...sanitizeParams(requestParams),
          ...makePaginationParams(rates),
          sessionId: window?.heap?.getSessionId?.() || '',
          memberUuid,
        })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      types.FETCH_PROVIDER_RATES_REQUEST,
      types.FETCH_PROVIDER_RATES_SUCCESS,
      types.FETCH_PROVIDER_RATES_ERROR,
    ],
  },
})

export const fetchPracticeRates = (requestParams, isHsaPlan) => ({
  [RSAA]: {
    endpoint: ({ Bundles: { rates } }) => {
      const memberUuid = webStorage.getItem('sub')
      return getApiPath(
        'doc',
        `bundle/rates/practices${makeQueryParams({
          ...sanitizeParams(requestParams),
          ...makePaginationParams(rates),
          memberUuid,
          sessionId: window?.heap?.getSessionId?.() || '',
        })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      types.FETCH_PRACTICE_RATES_REQUEST,
      {
        type: types.FETCH_PRACTICE_RATES_SUCCESS,
        meta: { practices: true, isHsaPlan },
      },
      types.FETCH_PRACTICE_RATES_ERROR,
    ],
  },
})

export const setApiCallId = apiCallId => {
  return { type: types.SET_API_CALL_ID, payload: { apiCallId } }
}

export const clearBundleRates = () => {
  return { type: types.CLEAR_RATES }
}

export default (state = initialState, { type, payload = {}, meta = {} }) => {
  switch (type) {
    case types.FETCH_PRACTICE_RATES_REQUEST:
    case types.FETCH_PROVIDER_RATES_REQUEST:
      return {
        ...state,
        rates: {
          ...state.rates,
          busy: true,
        },
      }
    case types.FETCH_PRACTICE_RATES_SUCCESS:
    case types.FETCH_PROVIDER_RATES_SUCCESS: {
      const {
        page,
        size,
        totalPages,
        providers,
        practices,
        bundle,
        apiCallId,
      } = payload

      const isLastPageReached =
        page === totalPages - 1 ||
        providers?.length === 0 ||
        practices?.length === 0

      // Transform practices to providers if needed
      const transformedProviders = meta.practices
        ? practices.map(practice =>
            transformPracticeToProvider(practice, meta.isHsaPlan),
          )
        : providers

      const newState = {
        ...state,
        apiCallId,
        rates: {
          ...state.rates,
          busy: false,
          page: page,
          size: size,
          isLastPageReached,
          entities: {
            providers: [
              ...(state?.rates?.entities?.providers || []),
              ...transformedProviders,
            ],
            bundleBenefitAmountData: bundle,
          },
        },
      }

      return newState
    }

    case types.FETCH_PRACTICE_RATES_ERROR:
    case types.FETCH_PROVIDER_RATES_ERROR:
      return {
        ...state,
        rates: {
          ...state.rates,
          busy: false,
          error: payload?.response?.message,
          // Set entities to a valid empty state so consuming components can
          // distinguish between "loading" (entities: null) and "error with no
          // data" (entities: { providers: [] }). This prevents error states from
          // being masked by loading state detection in components
          entities: { providers: [] },
        },
      }

    case types.SET_API_CALL_ID:
      return {
        ...state,
        apiCallId: payload.apiCallId,
      }

    case types.CLEAR_RATES:
      return {
        ...state,
        rates: {
          ...initialState.rates,
        },
      }
    default:
      return state
  }
}
